import { Alert, Box, CircularProgress, Container, Typography } from "@mui/material";
import { usePostHog } from "@posthog/react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../allauth/auth";
import InviteAthleteModal from "../components/invitations/InviteAthleteModal";
import InviteSupportModal from "../components/invitations/InviteSupportModal";
import OnboardingChecklist from "../components/onboarding/OnboardingChecklist";
import ApiClient from "../config/ApiClient";
import { useOnboarding } from "../context/OnboardingContext";
import {
    assignmentProgramProgressPath,
    type OnboardingStep,
} from "../utils/onboarding";
import RoleSelection from "./RoleSelection";

const OnboardingScreen: React.FC = () => {
    const user = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const posthog = usePostHog();
    const {
        assignmentId,
        assignment,
        currentStep,
        isComplete,
        loading,
        percent,
        participationRole,
        returnSearch,
        consumeReturn,
        clearReturn,
        refresh,
    } = useOnboarding();

    const viewTracked = useRef(false);
    useEffect(() => {
        if (loading || viewTracked.current || !posthog || !assignmentId) return;
        viewTracked.current = true;
        const params = new URLSearchParams(location.search);
        posthog.capture("onboarding_viewed", {
            assignment_id: assignmentId,
            current_step: currentStep?.key || null,
            is_complete: isComplete,
            percent,
            participation_role: participationRole,
            source: params.get("source") || params.get("utm_source") || null,
            utm_campaign: params.get("utm_campaign") || null,
        });
    }, [loading, posthog, assignmentId, currentStep, isComplete, percent, participationRole, location.search]);

    useEffect(() => {
        if (loading || !isComplete || !assignmentId) return;
        posthog?.capture("onboarding_completed", { percent, assignment_id: assignmentId });
        const carry = new URLSearchParams(returnSearch.replace(/^\?/, ""));
        let target = consumeReturn() || assignmentProgramProgressPath(assignmentId);
        if (carry.get("athlete_invite_accepted") === "1" && !target.includes("athlete_invite_accepted")) {
            target += (target.includes("?") ? "&" : "?") + "athlete_invite_accepted=1";
        }
        clearReturn();
        navigate(target, { replace: true });
    }, [loading, isComplete, percent, posthog, assignmentId, returnSearch, consumeReturn, clearReturn, navigate]);

    const [startingAi, setStartingAi] = useState(false);
    const [inviteAthleteOpen, setInviteAthleteOpen] = useState(false);
    const [inviteSupportOpen, setInviteSupportOpen] = useState(false);
    const [inviteGuardianOpen, setInviteGuardianOpen] = useState(false);
    const [inviteCoachOpen, setInviteCoachOpen] = useState(false);

    const handleStartWithAi = async () => {
        if (!assignmentId || startingAi) return;
        setStartingAi(true);
        posthog?.capture("start_program_with_ai_clicked", {
            assignment_id: assignmentId,
            source: "onboarding",
        });
        try {
            const res = await ApiClient.post(
                `/api/payment-assignments/${assignmentId}/start-with-ai/`,
                {},
            );
            if (res.success) {
                await refresh();
            }
        } finally {
            setStartingAi(false);
        }
    };

    const handleAction = (step: OnboardingStep) => {
        if (step.cta?.action === "open_waiver" && assignmentId) {
            posthog?.capture("onboarding_open_waiver", { step: step.key, assignment_id: assignmentId });
            window.dispatchEvent(
                new CustomEvent("smsp:open-waiver", {
                    detail: { role: participationRole, assignmentId },
                }),
            );
        } else if (step.cta?.action === "open_invite_athlete" && assignmentId) {
            if (step.cta.disabled) return;
            posthog?.capture("onboarding_open_invite_athlete", {
                step: step.key,
                assignment_id: assignmentId,
            });
            setInviteAthleteOpen(true);
        } else if (step.cta?.action === "open_invite_guardian" && assignmentId) {
            if (step.cta.disabled) return;
            posthog?.capture("onboarding_open_invite_guardian", {
                step: step.key,
                assignment_id: assignmentId,
            });
            setInviteGuardianOpen(true);
        } else if (step.cta?.action === "open_invite_coach" && assignmentId) {
            if (step.cta.disabled) return;
            posthog?.capture("onboarding_open_invite_coach", {
                step: step.key,
                assignment_id: assignmentId,
            });
            setInviteCoachOpen(true);
        }
    };

    if (!assignmentId) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">Assignment ID is required for onboarding.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
            </Box>
        );
    }

    if (!assignment) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">
                    Program Assignment not found or you do not have access. Contact your administrator if you need help.
                </Alert>
            </Container>
        );
    }

    if (currentStep?.key === "role") {
        return (
            <RoleSelection
                assignmentId={assignmentId}
                assignment={assignment}
                onSuccess={() => refresh()}
            />
        );
    }

    const firstName = user?.display?.split(" ")?.[0] || "";

    return (
        <Container maxWidth="md" sx={{ py: 4 }} data-attr="onboarding_screen" data-testid="onboarding-screen">
            <Box mb={3}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {firstName ? `Welcome, ${firstName}!` : "Welcome!"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    A few quick steps to finish setting up this assignment.
                </Typography>
            </Box>
            <OnboardingChecklist
                variant="full"
                onAction={handleAction}
                startingAi={startingAi}
                onInviteSupport={() => setInviteSupportOpen(true)}
                onStartWithAi={handleStartWithAi}
            />
            <InviteAthleteModal
                open={inviteAthleteOpen}
                assignmentId={assignmentId}
                onClose={() => {
                    setInviteAthleteOpen(false);
                    refresh();
                }}
                onInvited={() => refresh()}
            />
            <InviteSupportModal
                open={inviteSupportOpen}
                assignmentId={assignmentId}
                onClose={() => {
                    setInviteSupportOpen(false);
                    refresh();
                }}
                onInvited={() => refresh()}
            />
            <InviteSupportModal
                open={inviteGuardianOpen}
                assignmentId={assignmentId}
                allowedRoles={["parent"]}
                onClose={() => {
                    setInviteGuardianOpen(false);
                    refresh();
                }}
                onInvited={() => refresh()}
            />
            <InviteSupportModal
                open={inviteCoachOpen}
                assignmentId={assignmentId}
                allowedRoles={["coach"]}
                onClose={() => {
                    setInviteCoachOpen(false);
                    refresh();
                }}
                onInvited={() => refresh()}
            />
        </Container>
    );
};

export default OnboardingScreen;
